import { DictionaryLookupDS } from "@/dataSources/DictionaryLookupDS";
import { closePopup, reloadGrid } from "@/helper/DxHelper";
import { TEMPLATE_EXTENSIONS } from "@/helper/globals";
import { useDocumentTemplate } from "@/hooks/useDocumentTemplate";
import { useDocumentTemplateEdit } from "@/hooks/useDocumentTemplateEdit";
import { Dictionary } from "@/types/types";
import {
  Button,
  CheckBox,
  FileUploader,
  LoadIndicator,
  Lookup,
  Popup,
  TextArea,
  TextBox,
  ValidationGroup,
  ValidationSummary,
  Validator,
} from "devextreme-react";
import { DataGridRef } from "devextreme-react/cjs/data-grid";
import { PopupRef } from "devextreme-react/cjs/popup";
import { ValidationGroupRef } from "devextreme-react/cjs/validation-group";
import { RequiredRule } from "devextreme-react/validator";
import { ClickEvent } from "devextreme/ui/button";
import { ValueChangedEvent } from "devextreme/ui/file_uploader";
import { useEffect, useRef, useState } from "react";

type Props = {
  id: string | null;
  datagridRef: React.RefObject<DataGridRef>;
  popupRef: React.RefObject<PopupRef>;
  setEditId: React.Dispatch<React.SetStateAction<string | null>>;
};

export default function EditDocumentTemplate({ id, datagridRef, popupRef, setEditId }: Props) {
  const { data, isSuccess, isLoading, isRefetching, refetch } = useDocumentTemplate(id);
  const {
    mutate: editTemplate,
    isPending: isSaving,
    isSuccess: isSaved,
    data: editData,
  } = useDocumentTemplateEdit();
  const validationRef = useRef<ValidationGroupRef>(null);

  const [isVisible, setIsVisible] = useState(false);
  const [name, setName] = useState<string | undefined>();
  const [comment, setComment] = useState<string | undefined>();
  const [type, setType] = useState<Dictionary>();
  const [file, setFile] = useState<string>();
  const [fileName, setFileName] = useState<string | undefined>();
  const [isDefault, setIsDefault] = useState<boolean | undefined>(true);

  useEffect(() => {
    refetch();
  }, [id, refetch]);

  useEffect(() => {
    if (
      isSaved &&
      !data?.validation?.items?.length &&
      !editData?.validation?.items?.length &&
      !isSaving
    ) {
      closePopup(popupRef);
      setTimeout(() => {
        setEditId(null);
      }, 200);
    }
  }, [
    data?.validation?.items?.length,
    editData?.validation?.items?.length,
    isSaved,
    isSaving,
    popupRef,
    setEditId,
  ]);

  useEffect(() => {
    if (isSuccess && isVisible) {
      setName(data?.data?.name);
      setComment(data?.data?.comment);
      setType(data?.data?.templateType);
      setFileName(data?.data?.fileName);
      setIsDefault(data?.data?.isDefault);
    }
  }, [isSuccess, isVisible]);

  function submit(e: ClickEvent) {
    if (!e.validationGroup.validate().isValid) return;

    editTemplate({
      ID: id,
      Name: name,
      FileName: fileName,
      Comment: comment,
      TemplateTypeID: type?.id,
      FileValue: file,
      IsDefault: !!isDefault,
    });
  }

  function onFileChanged(e: ValueChangedEvent) {
    if (!e.value) return;

    const blob = e?.value[0];

    if (!blob) return;

    setFileName(blob?.name);

    const fr = new FileReader();
    fr.readAsDataURL(blob);

    fr.onload = () => {
      if (fr.result && typeof fr.result === "string") {
        setFile(fr.result);
      }
    };
  }

  function resetFields() {
    setName("");
    setComment("");
    setType(undefined);
    setIsDefault(false);
  }

  return (
    <Popup
      title="Edit Document Template"
      showCloseButton={true}
      ref={popupRef}
      onShowing={() => setIsVisible(true)}
      onHiding={() => {
        resetFields();
        validationRef.current?.instance()?.reset();
        reloadGrid(datagridRef);
        setIsVisible(false);
      }}
    >
      {isLoading || isRefetching ? (
        <LoadIndicator />
      ) : (
        <ValidationGroup ref={validationRef}>
          <form className="mx-1 grid grid-cols-2 gap-8">
            <TextBox
              value={name || ""}
              onValueChange={(value) => setName(value)}
              label="Name"
              labelMode="static"
              stylingMode="outlined"
              valueChangeEvent="keyup"
            >
              <Validator>
                <RequiredRule message={"Name is required"} />
              </Validator>
            </TextBox>
            <Lookup
              value={type}
              onValueChange={(value) => setType(value)}
              label="Type"
              displayExpr="name"
              dataSource={DictionaryLookupDS("90")}
              stylingMode="outlined"
              labelMode="static"
              searchEnabled={true}
              showClearButton={true}
            >
              <Validator>
                <RequiredRule message={"Type is required"} />
              </Validator>
            </Lookup>
            <TextArea
              value={comment || ""}
              onValueChange={(value) => setComment(value)}
              label="Comment"
              stylingMode="outlined"
              labelMode="static"
              valueChangeEvent="keyup"
            />
            <div>
              <label>
                <span className="font-semibold">Current file:</span> {fileName}
              </label>
              <FileUploader
                uploadMode={"useForm"}
                allowedFileExtensions={TEMPLATE_EXTENSIONS}
                onValueChanged={onFileChanged}
              />
            </div>
            <div className="col-start-2 mt-4">
              <CheckBox
                value={!!isDefault}
                onValueChange={(value) => setIsDefault(!!value)}
                text="Default"
              />
            </div>
          </form>
          <div className="absolute bottom-4 left-4">
            <ValidationSummary id="summary"></ValidationSummary>
          </div>
          <div className="absolute bottom-4 right-4">
            <Button
              useSubmitBehavior={false}
              type="default"
              height={40}
              width={isSaving ? 150 : 100}
              onClick={submit}
            >
              <LoadIndicator
                id="default-btn-loading"
                className="mr-3 inline-block align-middle"
                width={25}
                height={25}
                visible={isSaving}
              />
              <span className="dx-button-text">{isSaving ? "Updating" : "Update"}</span>
            </Button>
          </div>
        </ValidationGroup>
      )}
    </Popup>
  );
}
